import { KnownError } from "@/api/client";
import { useAdminSupportTickets } from "@/api/support";
import { TicketStatusChip } from "@/routes/admin/support/components/support-status-chip";
import { formatTicketDateTime } from "@/routes/admin/support/lib/format-datetime";
import { TICKET_STATUS_OPTIONS, ticketStatusDisplay } from "@/routes/admin/support/lib/status-display";
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  DropdownTriggerButton,
  Loading,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Typography,
} from "@backpac/monica";
import { useEffect, useState } from "react";
import { Link } from "react-router";

const PAGE_SIZE_FALLBACK = 20;

export function SupportInboxPage() {
  const [statusFilter, setStatusFilter] = useState<(typeof TICKET_STATUS_OPTIONS)[number] | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: statusFilter/debouncedSearch are the intended reset triggers, not read inside the effect
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  const { data, isLoading, isError, error, refetch, isFetching } = useAdminSupportTickets({
    status: statusFilter,
    q: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    page,
  });

  return (
    <div className="flex flex-col gap-6 max-w-[1080px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <Typography asChild size="xl" font="unbounded" color="textPrimary">
          <h1>Support tickets</h1>
        </Typography>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          className="w-full max-w-[320px]"
          placeholder="Search name, email, subject..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onReset={() => setSearchInput("")}
        />
        <Dropdown>
          <DropdownTrigger asChild>
            <DropdownTriggerButton size="md" className="min-w-[140px]">
              {statusFilter == null ? "All statuses" : ticketStatusDisplay[statusFilter].label}
            </DropdownTriggerButton>
          </DropdownTrigger>
          <DropdownContent align="start">
            <DropdownItem onSelect={() => setStatusFilter(undefined)}>All statuses</DropdownItem>
            {TICKET_STATUS_OPTIONS.map((status) => (
              <DropdownItem key={status} onSelect={() => setStatusFilter(status)}>
                {ticketStatusDisplay[status].label}
              </DropdownItem>
            ))}
          </DropdownContent>
        </Dropdown>
      </div>

      {isLoading ? <Loading className="py-16" /> : null}

      {isError ? <SupportInboxErrorState error={error} onRetry={() => refetch()} /> : null}

      {!isLoading && !isError && data != null ? (
        <>
          {data.items.length === 0 ? (
            <div className="rounded-[16px] border border-borderLight bg-white p-10 text-center">
              <Typography size="sm" color="textSecondary">
                No tickets match this search yet.
              </Typography>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>
                      <Link to={`/admin/support/${ticket.id}`} className="underline">
                        {ticket.reference}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate">{ticket.subject}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <Typography size="xs" weight={500}>
                          {ticket.name}
                        </Typography>
                        <Typography size="2xs" color="textMuted">
                          {ticket.email}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TicketStatusChip {...ticketStatusDisplay[ticket.status]} />
                    </TableCell>
                    <TableCell className="capitalize">{ticket.source}</TableCell>
                    <TableCell>{formatTicketDateTime(ticket.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <SupportInboxPagination
            page={data.page}
            pageSize={data.pageSize || PAGE_SIZE_FALLBACK}
            total={data.total}
            isFetching={isFetching}
            onChange={setPage}
          />
        </>
      ) : null}
    </div>
  );
}

function SupportInboxErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  // 401 is included here because the backend's staff guard returns a plain 401 for
  // "logged in, but not a staff account" too (see the `request()` comment in api/support.ts);
  // a real 401-with-no-session instead bubbles up to <AuthBoundary /> before it gets here.
  const isForbidden = KnownError.is(error) && (error.status === 403 || error.status === 401);
  const message = isForbidden
    ? "You're signed in, but this account isn't authorized to view the support inbox."
    : KnownError.is(error)
      ? error.message
      : "Couldn't load support tickets. Please try again.";

  return (
    <div className="rounded-[16px] bg-colorOrangeLight text-colorOrange p-6 flex items-center justify-between gap-4 flex-wrap">
      <Typography size="sm" className="text-colorOrange">
        {message}
      </Typography>
      {!isForbidden ? (
        <Button variant="outlined" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function SupportInboxPagination({
  page,
  pageSize,
  total,
  isFetching,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  onChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Pagination className="justify-between">
      <Typography size="xs" color="textMuted">
        {total} ticket{total === 1 ? "" : "s"} {isFetching ? "· loading..." : null}
      </Typography>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            disabled={page <= 1}
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) {
                onChange(page - 1);
              }
            }}
          />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink href="#" isActive>
            {page}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            href="#"
            disabled={page >= totalPages}
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) {
                onChange(page + 1);
              }
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
